/**
 * Token-keyed color-ramp resolution for choropleth overlays (M9).
 *
 * A choropleth NEVER stores or emits a raw hex. The classification engine emits
 * per-class STOPS carrying a `tokenId` (e.g. `map.ramp.sequential.3`), and the
 * persisted `overlay` block stores a `rampTokenId` naming a ramp FAMILY
 * (`map.ramp.sequential` / `map.ramp.diverging` / `map.ramp.heat`). Only here, at
 * render time, is a token resolved to a concrete hex — from the compiled token
 * artifact via `MapTokens` (the sanctioned literal home, DESIGN_SYSTEM.md). This
 * keeps `pnpm check:tokens` + `pnpm lint:brand` green: no ramp hex ever appears
 * in overlay source or in the saved spec.
 */

import type { MapTokens } from "@/lib/map";

/** The ramp FAMILY token ids a choropleth may draw from (all fixed, non-brand). */
export const RAMP_FAMILIES = {
  sequential: "map.ramp.sequential",
  diverging: "map.ramp.diverging",
  heat: "map.ramp.heat",
} as const;

export type RampFamilyTokenId = (typeof RAMP_FAMILIES)[keyof typeof RAMP_FAMILIES];

/** The default ramp family for a sequential quantity (population, income, …). */
export const DEFAULT_RAMP_FAMILY: RampFamilyTokenId = RAMP_FAMILIES.sequential;

/** Map a family token id to its resolved hex array in the active theme. */
function familyColors(family: string, tokens: MapTokens): string[] {
  switch (family) {
    case RAMP_FAMILIES.sequential:
      return tokens.sequential;
    case RAMP_FAMILIES.diverging:
      return tokens.diverging;
    case RAMP_FAMILIES.heat:
      return tokens.heat;
    default:
      throw new Error(`Unknown ramp family token id: ${family}`);
  }
}

/** Number of steps in a ramp family (drives class-count clamping). */
export function rampLength(family: string, tokens: MapTokens): number {
  return familyColors(family, tokens).length;
}

/** Compose a per-class stop token id from a family + a step index. */
export function stopTokenId(family: string, index: number): string {
  return `${family}.${index}`;
}

/** Split a stop token id back into its family + step index (inverse of `stopTokenId`). */
export function parseStopTokenId(tokenId: string): { family: string; index: number } {
  const dot = tokenId.lastIndexOf(".");
  const idx = Number(tokenId.slice(dot + 1));
  if (dot <= 0 || !Number.isInteger(idx)) {
    throw new Error(`Malformed ramp stop token id: ${tokenId}`);
  }
  return { family: tokenId.slice(0, dot), index: idx };
}

/**
 * Resolve a stop token id (family + index) to its concrete hex in `tokens`. The
 * index is clamped into the family's range so a class count above the ramp
 * length still resolves to the darkest step rather than throwing.
 */
export function resolveStopColor(tokenId: string, tokens: MapTokens): string {
  const { family, index } = parseStopTokenId(tokenId);
  const colors = familyColors(family, tokens);
  const clamped = Math.max(0, Math.min(index, colors.length - 1));
  return colors[clamped]!;
}

/**
 * Sample `classes` evenly-spaced step indices across a ramp family, returning the
 * per-class stop token ids in class order (light→dark). One class maps to index
 * 0; k classes spread across the family's full range.
 */
export function sampleRampStops(family: string, classes: number, tokens: MapTokens): string[] {
  const len = rampLength(family, tokens);
  const k = Math.max(1, classes);
  const out: string[] = [];
  for (let i = 0; i < k; i++) {
    const index = k === 1 ? 0 : Math.round((i * (len - 1)) / (k - 1));
    out.push(stopTokenId(family, index));
  }
  return out;
}
